import { Component } from '@angular/core';
import {
  SuperLatexAnnotation,
  SuperLatexComponent,
  SuperLatexConnection,
  SuperLatexContent,
  SuperLatexNode,
} from '@examdojo/super-latex';

@Component({
  selector: 'exam-super-latex-demo',
  standalone: true,
  imports: [SuperLatexComponent],
  templateUrl: './super-latex-demo.component.html',
  styleUrls: ['./super-latex-demo.component.scss'],
})
export class SuperLatexDemoComponent {
  readonly latex1: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=left-base}{(a + b)}^{\\htmlData{anchor=left-exp}{n}} = \\sum_{k=0}^{n} \\binom{n}{k} \\htmlData{anchor=term-ank}{a^{n-k}} b^k$`,
    },
  ];

  readonly latex2: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=example-base}{(\\htmlData{anchor=example-2x}{2x} + \\htmlData{anchor=example-3}{3})}^{\\htmlData{anchor=example-exp}{3}} \\quad | \\quad \\htmlData{anchor=general-base}{(\\htmlData{anchor=general-a}{a} + \\htmlData{anchor=general-b}{b})}^{\\htmlData{anchor=general-n}{n}}$`,
    },
  ];

  readonly latex3: SuperLatexNode[] = [
    {
      latex: `$\\frac{d}{dx}(\\htmlData{anchor=derivative-x3}{x^3} + {2x^2} - 5x + 1) = \\htmlData{anchor=result-3x2}{3x^2} + {4x} - \\htmlData{anchor=result-minus5}{5}$`,
    },
    {
      latex: `$\\htmlData{anchor=fprime}{f'(x)} = 3x^2 + 4x - 5$`,
    },
    {
      latex: `$\\htmlData{anchor=yprime}{y'} = 3x^2 + 4x - 5$`,
    },
  ];

  readonly annotations: SuperLatexAnnotation[] = [
    {
      id: 'example-annotation',
      target_anchor: 'example-2x',
      content: 'Highlighted coefficient',
      trigger: 'hover',
      placement: 'top',
    },
    {
      id: 'ank-annotation',
      target_anchor: 'term-ank',
      content: '\\text{Term with exponent } n-k',
      trigger: 'click',
      placement: 'top',
    },
  ];

  readonly connections: SuperLatexConnection[] = [
    {
      id: '2x-to-a',
      from_anchor: 'example-2x',
      to_anchor: 'general-a',
      style: {
        color: 'sky',
        width: 2,
        line_style: 'dashed',
        label: {
          text: '$a$ **coefficient**',
          style: 'square-box',
          offset: { x: -10, y: 10 },
        },
        from_socket: 'Bottom',
        to_socket: 'Bottom',
        stub: 15,
      },
    },
    {
      id: 'exp-to-n',
      from_anchor: 'example-exp',
      to_anchor: 'general-n',
      style: {
        color: 'red',
        width: 2,
        line_style: 'dashed',
        label: {
          text: '$n$ _exponent_',
          style: 'square-box',
        },
        from_socket: 'Top',
        to_socket: 'Top',
        stub: 15,
      },
    },
  ];

  readonly latex3Connections: SuperLatexConnection[] = [
    {
      id: 'x3-to-3x2',
      from_anchor: 'derivative-x3',
      to_anchor: 'result-3x2',
      style: {
        color: 'sky',
        width: 2,
        line_style: 'dashed',
        from_socket: 'Top',
        to_socket: 'Top',
        stub: 15,
      },
    },
    {
      id: 'fprime-to-minus5',
      from_anchor: 'fprime',
      to_anchor: 'result-minus5',
      style: {
        color: 'purple',
        width: 2,
        line_style: 'solid',
        from_socket: 'Top',
        to_socket: 'Bottom',
        stub: 20,
      },
    },
    {
      id: 'fprime-to-yprime',
      from_anchor: 'fprime',
      to_anchor: 'yprime',
      style: {
        color: 'green',
        width: 2,
        line_style: 'dashed',
        label: {
          text: "$f'(x) \\equiv y'$",
          style: 'square-box',
          location: 0.4,
          offset: { x: -10, y: 10 },
        },
        from_socket: 'Left',
        to_socket: 'Left',
        stub: 30,
      },
    },
  ];

  readonly latex4: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=expr1-left}{(2x + 3)^3} \\quad | \\quad \\htmlData{anchor=expr1-right}{(a + b)^n}$`,
    },
    {
      latex: `$\\htmlData{anchor=expr2-left}{(2x + 3)^3} \\quad | \\quad \\htmlData{anchor=expr2-right}{(a + b)^n}$`,
    },
  ];

  readonly latex4Connections: SuperLatexConnection[] = [
    {
      id: 'plus-connection',
      from_anchor: 'expr1-left',
      to_anchor: 'expr1-right',
      style: {
        color: 'indigo',
        width: 2,
        line_style: 'solid',
        label: {
          text: '$+$',
          style: 'round-box',
        },
        from_socket: 'Bottom',
        to_socket: 'Bottom',
        stub: 20,
      },
    },
    {
      id: 'equals-connection',
      from_anchor: 'expr2-left',
      to_anchor: 'expr2-right',
      style: {
        color: 'teal',
        width: 2,
        line_style: 'solid',
        label: {
          text: '$=$',
          style: 'round-box',
        },
        from_socket: 'Bottom',
        to_socket: 'Bottom',
        stub: 20,
      },
    },
  ];

  readonly latex5MenuExample: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=menu-expr1}{f(x)} = \\htmlData{anchor=menu-expr2}{g(x)}$`,
    },
  ];

  readonly latex5MenuConnections: SuperLatexConnection[] = [
    {
      id: 'menu-connection',
      from_anchor: 'menu-expr1',
      to_anchor: 'menu-expr2',
      style: {
        color: 'purple',
        width: 2,
        line_style: 'solid',
        label: {
          text: 'This is a **long explanation** that describes the relationship between $f(x)$ and $g(x)$. Click the pulsing dot to read more!',
          style: 'menu',
        },
        from_socket: 'Bottom',
        to_socket: 'Bottom',
        stub: 20,
      },
    },
  ];

  readonly latex5ColorExamples: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=color-2x}{2x} + \\htmlData{anchor=color-3}{3} = \\htmlData{anchor=color-11}{11}$`,
    },
    {
      latex: `$\\htmlData{anchor=color-a2}{a^2} + \\htmlData{anchor=color-b2}{b^2} = \\htmlData{anchor=color-c2}{c^2}$`,
    },
  ];

  readonly contentColor: SuperLatexContent = {
    latex_nodes: this.latex5ColorExamples,
    highlighted_anchors: {
      'color-2x': { style: 'text-color', color: 'sky' },
      'color-3': { style: 'text-color', color: 'orange' },
      'color-11': { style: 'text-color', color: 'green' },
      'color-a2': { style: 'text-color', color: 'red' },
      'color-b2': { style: 'text-color', color: 'indigo' },
      'color-c2': { style: 'text-color', color: 'purple' },
    },
  };

  readonly latex6HighlightExamples: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=highlight-2x}{2x} + \\htmlData{anchor=highlight-3}{3} = 11$`,
    },
    {
      latex: `$f(x) = \\htmlData{anchor=highlight-x2}{x^2} + \\htmlData{anchor=highlight-2x2}{2x} + \\htmlData{anchor=highlight-1}{1}$`,
    },
  ];

  readonly contentHighlight: SuperLatexContent = {
    latex_nodes: this.latex6HighlightExamples,
    highlighted_anchors: {
      'highlight-2x': { style: 'text-color-with-background', color: 'sky' },
      'highlight-3': { style: 'text-color-with-background', color: 'orange' },
      'highlight-x2': { style: 'text-color-with-background', color: 'teal' },
      'highlight-2x2': { style: 'text-color-with-background', color: 'pink' },
      'highlight-1': { style: 'text-color-with-background', color: 'amber' },
    },
  };

  readonly latex7MixedColoringExamples: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=mixed-base}{(\\htmlData{anchor=mixed-2x}{2x} + \\htmlData{anchor=mixed-3}{3})}^{\\htmlData{anchor=mixed-exp}{3}}$`,
    },
    {
      latex: `$\\frac{\\htmlData{anchor=mixed-d}{d}}{\\htmlData{anchor=mixed-dx}{dx}}(\\htmlData{anchor=mixed-x3}{x^3}) = \\htmlData{anchor=mixed-3x2}{3x^2}$`,
    },
  ];

  readonly contentMixed: SuperLatexContent = {
    latex_nodes: this.latex7MixedColoringExamples,
    highlighted_anchors: {
      'mixed-base': { style: 'text-color', color: 'sky' },
      'mixed-2x': { style: 'highlighter' },
      'mixed-3': { style: 'highlighter' },
      'mixed-exp': { style: 'text-color', color: 'green' },
      'mixed-d': { style: 'highlighter' },
      'mixed-dx': { style: 'text-color', color: 'purple' },
      'mixed-x3': { style: 'text-color', color: 'teal' },
      'mixed-3x2': { style: 'highlighter' },
    },
  };

  readonly equalsAlignmentTest1: SuperLatexNode[] = [
    { latex: `$x = 5$` },
    { latex: `$y = 10$` },
    { latex: `$z = 15$` },
  ];

  readonly equalsAlignmentTest2: SuperLatexNode[] = [
    { latex: `$x = 5$` },
    { latex: `$xy = 50$` },
    { latex: `$xyz = 750$` },
  ];

  readonly equalsAlignmentTest3: SuperLatexNode[] = [
    { latex: `$a + b = c$` },
    { latex: `$x^2 + 2xy + y^2 = (x + y)^2$` },
    { latex: `$E = mc^2$` },
  ];

  readonly equalsAlignmentTest4: SuperLatexNode[] = [
    { latex: `$\\frac{d}{dx}(x^3 + 2x^2 - 5x + 1) = 3x^2 + 4x - 5$` },
    { latex: `$f'(x) = 3x^2 + 4x - 5$` },
    { latex: `$y' = 3x^2 + 4x - 5$` },
  ];

  readonly equalsAlignmentTest5: SuperLatexNode[] = [
    { latex: `$\\sin^2(\\theta) + \\cos^2(\\theta) = 1$` },
    { latex: `$e^{i\\pi} + 1 = 0$` },
    { latex: `$a = b$` },
    { latex: `$\\int_{0}^{\\infty} e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}$` },
  ];

  readonly equalsAlignmentTest6: SuperLatexNode[] = [
    { latex: `$\\lim_{x \\to 0} \\frac{\\sin(x)}{x} = 1$` },
    { latex: `$f(x) = x^2 + 3x + 2$` },
    { latex: `$\\nabla \\times \\vec{E} = -\\frac{\\partial \\vec{B}}{\\partial t}$` },
  ];

  readonly quadraticCoefficients: SuperLatexNode[] = [
    {
      latex: `$\\underset{\\htmlData{anchor=quad-a-label}{= a}}{\\htmlData{anchor=quad-a}{3}}x^2 - \\underset{\\htmlData{anchor=quad-b-label}{= b}}{\\htmlData{anchor=quad-b}{8}}x + \\underset{\\htmlData{anchor=quad-c-label}{= c}}{\\htmlData{anchor=quad-c}{4}} = 0$`,
    },
  ];

  readonly pascalTriangle: SuperLatexNode[] = [
    {
      latex: `$\\begin{array}{c} 1 \\\\ 1 \\quad 1 \\\\ 1 \\quad 2 \\quad 1 \\\\ 1 \\quad 3 \\quad 3 \\quad 1 \\\\ \\htmlData{anchor=pascal-1}{1} \\quad \\htmlData{anchor=pascal-4}{4} \\quad \\htmlData{anchor=pascal-6}{6} \\quad 4 \\quad 1 \\end{array}$`,
    },
    {
      latex: `$(2x + 1)^4 = \\htmlData{anchor=term-1}{1}(2x)^4(1)^0 + \\htmlData{anchor=term-4}{4}(2x)^3(1)^1 + \\htmlData{anchor=term-6}{6}(2x)^2(1)^2 + \\ldots$`,
    },
  ];

  readonly pascalTriangleConnections: SuperLatexConnection[] = [
    {
      id: 'pascal-1-to-term-1',
      from_anchor: 'pascal-1',
      to_anchor: 'term-1',
      style: {
        color: 'sky',
        width: 2,
        line_style: 'solid',
        from_socket: 'Bottom',
        to_socket: 'Top',
        stub: 20,
      },
    },
    {
      id: 'pascal-4-to-term-4',
      from_anchor: 'pascal-4',
      to_anchor: 'term-4',
      style: {
        color: 'amber',
        width: 2,
        line_style: 'solid',
        from_socket: 'Bottom',
        to_socket: 'Top',
        stub: 20,
      },
    },
    {
      id: 'pascal-6-to-term-6',
      from_anchor: 'pascal-6',
      to_anchor: 'term-6',
      style: {
        color: 'pink',
        width: 2,
        line_style: 'solid',
        from_socket: 'Bottom',
        to_socket: 'Top',
        stub: 20,
      },
    },
  ];

  readonly nativeKatexArrows: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=arrow-4}{4}, \\xrightarrow{\\htmlData{anchor=arrow-mult1}{\\cdot 3}} 12, \\xrightarrow{\\htmlData{anchor=arrow-mult2}{\\cdot 3}} 36, \\xrightarrow{\\htmlData{anchor=arrow-mult3}{\\cdot 3}} 108, \\ldots$`,
    },
  ];

  readonly superLatexSequence: SuperLatexNode[] = [
    {
      latex: `$\\htmlData{anchor=seq-4}{4}, \\; \\htmlData{anchor=seq-12}{12}, \\; \\htmlData{anchor=seq-36}{36}, \\; \\htmlData{anchor=seq-108}{108}, \\ldots$`,
    },
  ];

  readonly superLatexSequenceConnections: SuperLatexConnection[] = [
    {
      id: 'seq-4-to-12',
      from_anchor: 'seq-4',
      to_anchor: 'seq-12',
      style: {
        color: 'sky',
        width: 1.2,
        line_style: 'solid',
        label: {
          text: '$\\cdot 3$',
          style: 'text-only',
        },
        from_socket: 'Top',
        to_socket: 'Top',
        stub: 15,
      },
    },
    {
      id: 'seq-12-to-36',
      from_anchor: 'seq-12',
      to_anchor: 'seq-36',
      style: {
        color: 'sky',
        width: 1.2,
        line_style: 'solid',
        label: {
          text: '$\\cdot 3$',
          style: 'text-only',
        },
        from_socket: 'Top',
        to_socket: 'Top',
        stub: 15,
      },
    },
    {
      id: 'seq-36-to-108',
      from_anchor: 'seq-36',
      to_anchor: 'seq-108',
      style: {
        color: 'sky',
        width: 1.2,
        line_style: 'solid',
        label: {
          text: '$\\cdot 3$',
          style: 'text-only',
        },
        from_socket: 'Top',
        to_socket: 'Top',
        stub: 15,
      },
    },
  ];

  readonly content1: SuperLatexContent = {
    latex_nodes: this.latex1,
    annotations: this.annotations,
    connections: [],
  };

  readonly content2: SuperLatexContent = {
    latex_nodes: this.latex2,
    annotations: this.annotations,
    connections: this.connections,
    highlighted_anchors: {
      'example-2x': { style: 'text-color-with-background', color: 'sky' },
    },
  };

  readonly content3: SuperLatexContent = {
    latex_nodes: this.latex3,
    connections: this.latex3Connections,
  };

  readonly content4: SuperLatexContent = {
    latex_nodes: this.latex4,
    connections: this.latex4Connections,
  };

  readonly content5Menu: SuperLatexContent = {
    latex_nodes: this.latex5MenuExample,
    connections: this.latex5MenuConnections,
  };

  readonly contentQuadraticCoefficients: SuperLatexContent = {
    latex_nodes: this.quadraticCoefficients,
    highlighted_anchors: {
      'quad-a': { style: 'text-color', color: 'sky' },
      'quad-a-label': { style: 'text-color', color: 'sky' },
      'quad-b': { style: 'text-color', color: 'red' },
      'quad-b-label': { style: 'text-color', color: 'red' },
      'quad-c': { style: 'text-color', color: 'amber' },
      'quad-c-label': { style: 'text-color', color: 'amber' },
    },
  };

  readonly contentPascalTriangle: SuperLatexContent = {
    latex_nodes: this.pascalTriangle,
    connections: this.pascalTriangleConnections,
    layout: {
      align_text_block: 'center',
      align_on_equals: false,
      align_text: 'center',
    },
    highlighted_anchors: {
      'pascal-1': { style: 'text-color-with-background', color: 'sky' },
      'pascal-4': { style: 'text-color-with-background', color: 'amber' },
      'pascal-6': { style: 'text-color-with-background', color: 'pink' },
      'term-1': { style: 'text-color-with-background', color: 'sky' },
      'term-4': { style: 'text-color-with-background', color: 'amber' },
      'term-6': { style: 'text-color-with-background', color: 'pink' },
    },
  };

  readonly contentNativeKatexArrows: SuperLatexContent = {
    latex_nodes: this.nativeKatexArrows,
    highlighted_anchors: {
      'arrow-4': { style: 'text-color', color: 'red' },
      'arrow-mult1': { style: 'text-color', color: 'sky' },
      'arrow-mult2': { style: 'text-color', color: 'sky' },
      'arrow-mult3': { style: 'text-color', color: 'sky' },
    },
  };

  readonly contentSuperLatexSequence: SuperLatexContent = {
    latex_nodes: this.superLatexSequence,
    connections: this.superLatexSequenceConnections,
    highlighted_anchors: {
      'seq-4': { style: 'text-color', color: 'red' },
    },
  };
}
